import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Popover,
  Tooltip,
  Typography
} from '@mui/material'
import {
  DataGridPro,
  GridAlignment,
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridValueFormatterParams
} from '@mui/x-data-grid-pro'
import dayjs, { Dayjs } from 'dayjs'
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'

import { sdk } from '../../config'
import useAccessTokenAndShopId from '../../hooks/useAccessTokenAndShopId/useAccessTokenAndShopId'
import { NotificationContext } from '../../providers'
import { useCreateAutomatedVehicleReminderMutation } from '../../redux/api/vehicleApi'
import {
  AutomatedVehicleReminderInfoTimeframeEnum,
  ResponseError,
  Rrule,
  RruleBydayEnum,
  RruleFreqEnum,
  VehicleReminder,
  VehicleReminderMessageInfoTypeEnum
} from '../../sdk'
import { describeVehicle } from '../../utils'
import {
  getDateDetails,
  numberToDayEnum
} from '../../utils/dateMappings/dateMappings'
import { defaultReminderMessage } from '../../utils/defaultReminderMessage/defaultReminderMessage'
import { formatName } from '../../utils/formatCustomerName/formatCustomerName'
import formatMdyDate from '../../utils/formatMdyDate/formatMdyDate'
import {
  calculateNextOccurrence,
  generateRruleString
} from '../../utils/generateRrule/generateRrule'
import { getTimeframeStartEnd } from '../../utils/getTimeframeStartEnd/getTimeframeStartEnd'
import { RepeatSendPicker } from '../SharedUI/RepeatSendPicker'
import { useDataGridHover } from '../SharedUI/useDataGridHover'
import { VehicleReminderFilterForm } from './VehicleReminderFilterForm'

interface Props {
  open: boolean
  handleClose: () => void
  vehicleReminders: VehicleReminder[]
}

export const AutomatedSendReminderCreateDialog: FC<Props> = ({
  open,
  handleClose,
  vehicleReminders,
  ...props
}) => {
  const { accessToken, shopId } = useAccessTokenAndShopId()
  const { setNotice } = useContext(NotificationContext)

  const [createAutomatedVehicleReminder] =
    useCreateAutomatedVehicleReminderMutation()

  const [activeStep, setActiveStep] = useState(0)
  const handleNext = () => {
    if (activeStep === 0) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }
  }
  const handleBack = () => {
    if (activeStep === 1) {
      setActiveStep((prevActiveStep) => prevActiveStep - 1)
    }
  }

  const [reminder, setReminder] = useState<VehicleReminderMessageInfoTypeEnum>(
    VehicleReminderMessageInfoTypeEnum.Inspection
  )
  const [timeframe, setTimeframe] =
    useState<AutomatedVehicleReminderInfoTimeframeEnum>(
      AutomatedVehicleReminderInfoTimeframeEnum.NextWeek
    )

  // need useMemo here else memory leak
  const [start, end] = useMemo(() => {
    return getTimeframeStartEnd(timeframe)
  }, [timeframe])

  const filteredReminders = useMemo(() => {
    return (vehicleReminders || [])
      .filter((vr) => {
        const reminderDate =
          reminder === VehicleReminderMessageInfoTypeEnum.Inspection
            ? vr.vehicleEntry?.info?.nextInspectionDate
            : vr.vehicleEntry?.info?.nextOilServiceDate

        if (!reminderDate) {
          return false // Skip if there's no associated date
        }

        return reminderDate >= start && reminderDate <= end
      })
      .filter((vr) => !vr.customerEntries![0]!.information!.marketingOptOut)
  }, [end, reminder, start, vehicleReminders])

  const [phoneStatuses, setPhoneStatuses] = useState<Record<string, string>>({})

  useEffect(() => {
    const newPhoneStatuses: Record<string, string> = {}

    const promises = filteredReminders.map(async (vr) => {
      const phoneNumber = vr.customerEntries![0]!.information!.phone!
      try {
        await sdk.lookupPhoneNumber({
          authorization: accessToken,
          shop: shopId,
          phoneNumber
        })
        newPhoneStatuses[vr.vehicleEntry!.id!] = 'Ready'
      } catch (error) {
        if (error instanceof ResponseError) {
          const errorText = await error.response.text()
          const errorMessage = JSON.parse(errorText).message
          if (errorMessage.includes('Invalid phone number')) {
            newPhoneStatuses[vr.vehicleEntry!.id!] = errorMessage
          } else {
            throw error
          }
        }
      }
    })

    Promise.all(promises).then(() =>
      setPhoneStatuses((prevStatuses) => ({
        ...prevStatuses,
        ...newPhoneStatuses
      }))
    )
  }, [accessToken, filteredReminders, shopId])

  const [dtstart, setDtstart] = useState<Dayjs | null>(dayjs(new Date()))
  const [repeatFrequency, setRepeatFrequency] = useState<number>(1)
  const [repeatFrequencyUnit, setRepeatFrequencyUnit] = useState<RruleFreqEnum>(
    RruleFreqEnum.Weekly
  )
  const [selectedByday, setSelectedByday] = useState<RruleBydayEnum>(
    dtstart !== null ? numberToDayEnum(dtstart.day()) : RruleBydayEnum.Mo
  )
  const [monthlyType, setMonthlyType] = useState<'date' | 'day'>('date')
  const [repeatError, setRepeatError] = useState<boolean>(false)

  const rrule = useMemo(() => {
    if (repeatError) {
      return undefined
    }

    return {
      dtstart: dtstart!.startOf('day').toDate()!,
      freq: repeatFrequencyUnit,
      interval: repeatFrequency,
      byday:
        repeatFrequencyUnit === RruleFreqEnum.Weekly
          ? selectedByday
          : repeatFrequencyUnit === RruleFreqEnum.Monthly &&
            monthlyType === 'day'
          ? (('_' + getDateDetails(dtstart, true)) as RruleBydayEnum)
          : undefined,
      bymonthday:
        repeatFrequencyUnit === RruleFreqEnum.Monthly && monthlyType === 'date'
          ? dtstart!.date()
          : undefined
    } as Rrule
  }, [
    dtstart,
    monthlyType,
    repeatError,
    repeatFrequency,
    repeatFrequencyUnit,
    selectedByday
  ])

  const handleCreate = useCallback(async () => {
    if (repeatError || !rrule) {
      return
    }

    await createAutomatedVehicleReminder({
      authorization: accessToken,
      automatedVehicleReminderInfo: {
        shop: shopId,
        type: reminder,
        timeframe,
        rrule
      }
    })

    setNotice('Successfully created automated vehicle reminder')

    handleClose()
  }, [
    repeatError,
    createAutomatedVehicleReminder,
    accessToken,
    shopId,
    reminder,
    timeframe,
    rrule,
    setNotice,
    handleClose
  ])

  const columns = useMemo<GridColDef[]>(() => {
    return [
      {
        field: 'ymm',
        headerName: 'YMM',
        flex: 2
      },
      {
        field: 'customer',
        headerName: 'Customer',
        flex: 2
      },
      {
        field: 'date',
        headerName: 'Date',
        flex: 1,
        type: 'date',
        valueFormatter: (params: GridValueFormatterParams) =>
          params.value ? formatMdyDate(params.value as Date) : ''
      },
      {
        field: 'status',
        type: 'actions',
        headerName: 'Status',
        headerAlign: 'left' as GridAlignment,
        align: 'center' as GridAlignment,
        width: 130,
        renderCell: (params: GridRenderCellParams) => {
          switch (params.value) {
            case 'Loading':
              return (
                <Tooltip title={params.value}>
                  <Chip label={params.value} color="warning" />
                </Tooltip>
              )

            case 'Ready':
              return (
                <Tooltip title={params.value}>
                  <Chip label={params.value} color="success" />
                </Tooltip>
              )

            default:
              return (
                <Tooltip title={params.value}>
                  <Chip label="Error" color="error" />
                </Tooltip>
              )
          }
        }
      }
    ]
  }, [])

  const rows: GridRowsProp = useMemo(() => {
    return filteredReminders.map((vr) => ({
      id: vr.vehicleEntry?.id,
      ymm: describeVehicle(vr.vehicleEntry?.info),
      customer: formatName(vr.customerEntries![0]!.information!),
      date:
        reminder === VehicleReminderMessageInfoTypeEnum.Inspection
          ? vr.vehicleEntry?.info?.nextInspectionDate
          : vr.vehicleEntry?.info?.nextOilServiceDate,
      status: phoneStatuses[vr.vehicleEntry!.id!] ?? 'Loading'
    }))
  }, [filteredReminders, phoneStatuses, reminder])

  const { popOverProps, handlePopoverClose, setValue, setAnchorEl } =
    useDataGridHover()

  const handlePopoverOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      const field = event.currentTarget.dataset.field!
      const id = event.currentTarget.parentElement!.dataset.id!

      if (field !== 'status' && field !== 'actions') {
        const vehicleReminder = filteredReminders.find(
          (vr) => vr.vehicleEntry?.id === id
        )!

        const message = defaultReminderMessage(
          vehicleReminder.customerEntries![0]!,
          vehicleReminder.vehicleEntry,
          reminder
        )

        setValue(message)
        setAnchorEl(event.currentTarget)
      }
    },
    [filteredReminders, reminder, setAnchorEl, setValue]
  )

  return (
    <Dialog
      {...props}
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>Schedule Reminder</DialogTitle>
      <DialogContent sx={{ pb: 0, height: 315 }}>
        <Typography pb={2}>
          Modify the filters to define the recurring reminder
        </Typography>

        {activeStep === 0 && (
          <>
            <VehicleReminderFilterForm
              reminder={reminder}
              setReminder={setReminder}
              timeframe={timeframe}
              setTimeframe={setTimeframe}
            />

            <FormHelperText>Preview: </FormHelperText>
            <DataGridPro
              density="compact"
              rows={rows}
              columns={columns}
              autoHeight
              hideFooter
              slotProps={{
                cell: {
                  onMouseEnter: handlePopoverOpen,
                  onMouseLeave: handlePopoverClose,
                  onClick: handlePopoverClose // needed because anchorEl disables editing on hover
                }
              }}
            />
            <Popover {...popOverProps} />
          </>
        )}

        {activeStep === 1 && (
          <>
            <RepeatSendPicker
              dtstart={dtstart}
              setDtstart={setDtstart}
              repeatFrequency={repeatFrequency}
              setRepeatFrequency={setRepeatFrequency}
              repeatFrequencyUnit={repeatFrequencyUnit}
              setRepeatFrequencyUnit={setRepeatFrequencyUnit}
              selectedByday={selectedByday}
              setSelectedByday={setSelectedByday}
              monthlyType={monthlyType}
              setMonthlyType={setMonthlyType}
              setError={setRepeatError}
            />

            {!repeatError &&
              dtstart !== null &&
              dtstart?.toString() !== 'Invalid Date' &&
              rrule && (
                <Box
                  display="flex"
                  justifyContent="center"
                  pt={2}
                  alignItems="center">
                  <Typography>
                    Next Send:{' '}
                    {calculateNextOccurrence(
                      dtstart!.toDate()!,
                      generateRruleString(rrule)
                    )?.toDateString()}
                  </Typography>
                  <Tooltip title={'Will be sent at 10am local time'}>
                    <InfoOutlinedIcon fontSize="small" sx={{ pl: 1 }} />
                  </Tooltip>
                </Box>
              )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ pt: 2 }}>
        {activeStep === 0 && (
          <>
            <Button variant="text" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="text" onClick={handleNext}>
              Next
              <KeyboardArrowRight />
            </Button>
          </>
        )}

        {activeStep === 1 && (
          <>
            <Button variant="text" onClick={handleBack}>
              <KeyboardArrowLeft />
              Back
            </Button>
            <Button
              variant="text"
              onClick={handleCreate}
              disabled={repeatError || !rrule}>
              Create
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
